import React from 'react';
import { Box, Container, Paper, Typography, useTheme, Divider, List, ListItem, ListItemText } from '@mui/material';
import { tokens } from '../../theme';

const AccountDeletion = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.primary[400],
        p: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md" style={{ margin: 'auto' }}>
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark' ? colors.primary[500] : '#ffffff',
            border: `1px solid ${colors.primary[600]}`,
          }}
        >
          {/* Title */}
          <Box mb={4} textAlign="center">
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                color: colors.grey[100],
                letterSpacing: 0.5,
                fontSize: { xs: '1.9rem', md: '2.4rem' },
              }}
            >
              Account Deletion
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.greenAccent[400],
                fontSize: { xs: '0.95rem', md: '1.1rem' },
              }}
            >
              We're sorry to see you go.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* When you delete your account */}
          <Box
            sx={{
              mb: 4,
              p: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' ? colors.primary[600] : '#f3f8ff',
              borderLeft: `4px solid ${colors.blueAccent[400]}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.2rem', md: '1.4rem' },
                mb: 1.5,
                color: colors.grey[100],
              }}
            >
              When you delete your account:
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1.75, color: colors.grey[100], lineHeight: 1.7, fontSize: { xs: '0.95rem', md: '1rem' } }}
            >
              Your profile and all personal information will be permanently removed from our system.
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1.75, color: colors.grey[100], lineHeight: 1.7, fontSize: { xs: '0.95rem', md: '1rem' } }}
            >
              We will not retain any of your personal data after deletion.
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: colors.grey[200], lineHeight: 1.7, fontSize: { xs: '0.95rem', md: '1rem' } }}
            >
              Technical logs may be kept for up to 30 days for security and audit purposes. These logs do not contain
              personal data.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[200], lineHeight: 1.7, fontSize: { xs: '0.95rem', md: '1rem' } }}
            >
              If you ever change your mind, you're welcome to rejoin anytime by simply registering again.
            </Typography>
          </Box>

          {/* How to delete your account */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: colors.grey[100],
              }}
            >
              How to Delete Your Account:
            </Typography>
            <List
              sx={{
                listStyleType: 'decimal',
                pl: 3,
                mb: 2.5,
                backgroundColor: theme.palette.mode === 'dark' ? colors.primary[600] : '#fdfdfd',
                borderRadius: 2,
                border: `1px solid ${colors.grey[700]}`,
              }}
            >
              {['Log in to the app', 'Open your settings', 'Tap on Delete Account', 'Confirm your choice'].map(
                (text, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      display: 'list-item',
                      py: 0.75,
                      color: colors.grey[100],
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: { xs: '0.95rem', md: '1.05rem' },
                      }}
                      primary={text}
                    />
                  </ListItem>
                ),
              )}
            </List>

            <Typography
              variant="body1"
              sx={{ color: colors.grey[100], fontSize: { xs: '0.95rem', md: '1.05rem' }, lineHeight: 1.7 }}
            >
              Once confirmed, your account will be permanently removed.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AccountDeletion;
